import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '~/constants/colors';
import SpinLoading from '../../components/Loading/SpinLoading';
import {
  ADD_REVIEW_DIRECT_TO_SOLV,
  ADD_REVIEW_FLOW_RECOMMEND_TO_GOOGLE,
  ADD_REVIEW_THANK_YOU,
  ADD_REVIEW_UNGATED,
  ADD_REVIEW_UNGATED_REVIEW_ON_GOOGLE_OR_SOLV,
  ADD_SOLV_REVIEW_WOULD_NOT_RECOMMEND,
  ADD_SOLV_REVIEW_WOULD_RECOMMEND,
  NPS_AND_ADD_REVIEW_FLOW_RECOMMEND_TO_GOOGLE,
} from '../../constants/index';
import NpsAndRecommendToGoogle from './flows/NpsAndRecommendToGoogle';
import RecommendToGoogle from './flows/RecommendToGoogle';
import RecommendToSolv from './flows/RecommendToSolv';
import ReviewOnSolv from './flows/ReviewOnSolv';
import UngatedReview from './flows/UngatedReview';
import UngatedReviewOnGoogleOrSolv from './flows/UngatedReviewOnGoogleOrSolv';

import BorderBoxReset from '~/components/util/BorderBoxReset';
import { campaignNames } from '~/core/analytics/events';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import useSetTitle from '~/hooks/useSetTitle';
import DownloadDriver from '../../components/DownloadDriver';
import Layout from '../../components/Layout';
import { ReviewContext, ReviewProps } from './context';

const title = 'Rate & Review';
const WOULD_RECOMMEND_STAR_RATING = 5;
const WOULD_NOT_RECOMMEND_STAR_RATING = 3;

const AddReviewRoot = styled.div.withConfig({ componentId: 'sc-p1mon4-0' })`
  margin: 0 auto;
  overflow-x: hidden;
  background: ${colors.white};
  height: 100vh;
  position: relative;
  text-align: center;
`;

const AddReview = (props: ReviewProps, context: any) => {
  const {
    bookingId,
    flowState,
    flowType,
    getBooking,
    isModal,
    location,
    persistentStateLoaded,
    queryParamFlowType,
    starRating,
  } = props;

  const [loading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    getBooking(bookingId);
    context.setMeta('theme-color', colors.mrBlueSky);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsLoading(false);
  }, [location]);

  const isReviewFlowOfType = (type: string) => flowType === type;

  const isReviewFlowNotSpecified = () =>
    'reviewFlowType' in location && !location.reviewFlowType && !queryParamFlowType;

  const renderRecommendToGoogleFlow = () => <RecommendToGoogle />;

  const renderNpsAndRecommendToGoogleFlow = () => <NpsAndRecommendToGoogle />;

  const renderWouldRecommendFlow = () => (
    <ReviewOnSolv starRating={starRating || WOULD_RECOMMEND_STAR_RATING} />
  );

  const renderWouldNotRecommendFlow = () => (
    <ReviewOnSolv starRating={starRating || WOULD_NOT_RECOMMEND_STAR_RATING} />
  );

  const renderUngatedReviewOnGoogleOrSolv = () => <UngatedReviewOnGoogleOrSolv />;

  const renderUngated = () => <UngatedReview />;

  const renderLeaveReviewOnSolv = () => <RecommendToSolv />;

  useSetTitle(title);

  const isMobile = useBreakpoint('tablet', 'below');

  if (!persistentStateLoaded || isEmpty(location) || loading) {
    return <SpinLoading />;
  }

  const reviewContent = (
    <BorderBoxReset>
      <ReviewContext.Provider value={props}>
        {flowState === ADD_REVIEW_THANK_YOU && (
          <DownloadDriver campaignName={campaignNames.END_OF_REVIEW} isCarousel />
        )}
        <AddReviewRoot>
          {isReviewFlowOfType(NPS_AND_ADD_REVIEW_FLOW_RECOMMEND_TO_GOOGLE) &&
            renderNpsAndRecommendToGoogleFlow()}
          {isReviewFlowOfType(ADD_SOLV_REVIEW_WOULD_RECOMMEND) && renderWouldRecommendFlow()}
          {isReviewFlowOfType(ADD_SOLV_REVIEW_WOULD_NOT_RECOMMEND) && renderWouldNotRecommendFlow()}
          {isReviewFlowOfType(ADD_REVIEW_UNGATED_REVIEW_ON_GOOGLE_OR_SOLV) &&
            renderUngatedReviewOnGoogleOrSolv()}
          {isReviewFlowOfType(ADD_REVIEW_UNGATED) && renderUngated()}
          {isReviewFlowOfType(ADD_REVIEW_DIRECT_TO_SOLV) && renderLeaveReviewOnSolv()}
          {(isReviewFlowOfType(ADD_REVIEW_FLOW_RECOMMEND_TO_GOOGLE) ||
            isReviewFlowNotSpecified()) &&
            renderRecommendToGoogleFlow()}
        </AddReviewRoot>
      </ReviewContext.Provider>
    </BorderBoxReset>
  );

  if (isModal) {
    return (
      <>
        <Layout
          hideFooter={isModal}
          showBackArrow={!isModal}
          showCloseXRight={isModal}
          showLogo={!isModal}
          showSiteMenu={!isModal}
          title="Leave a Review"
        >
          {reviewContent}
        </Layout>
      </>
    );
  }

  return reviewContent;
};

AddReview.defaultProps = {
  isModal: false,
};

AddReview.contextTypes = {
  setMeta: PropTypes.func.isRequired,
};

export default AddReview;
